import { AllEntitiesResolved, Entity, mapAssetClass } from '@api';
import { WithNAFallback } from '@design';
import { ColumnDef, Table } from '@tanstack/react-table';
import { RowSelectionCheckbox } from '@terminal/components/row-selection-checkbox/RowSelectionCheckbox';
import { TruncateTooltip } from '@terminal/components/truncate-tooltip/TruncateTooltip';
import { TFunction } from 'i18next';
import React, { CSSProperties } from 'react';

import {
  EntityCell,
  EntityCellBaseHandlers,
} from './table-cells/entity-cell/EntityCell';
import styles from './TableWatchlistManager.module.scss';

export const INPUT_HEADER_COLUMN_ID = 'input';

interface GetColumnsProps {
  t: TFunction;
  optionalColumns: { inputColumn: boolean; industryColumn: boolean };
  toggleAllRowsSelection: (checked: boolean) => void;
  handlers: EntityCellBaseHandlers;
}

export const getColumns = ({
  t,
  optionalColumns,
  toggleAllRowsSelection,
  handlers,
}: GetColumnsProps) => {
  const inputColumn: ColumnDef<AllEntitiesResolved> = {
    id: INPUT_HEADER_COLUMN_ID,
    header: t('watchlistManager:inputColumnLabel'),
    cell: props => {
      const { input, tag } = props.row.original;
      const parsedInput = input || '-';

      const inputLabel = (
        <span
          className={styles.text}
          style={
            {
              '--text-color': tag
                ? 'var(--text-default)'
                : 'var(--text-critical)',
            } as CSSProperties
          }
        >
          {parsedInput}
        </span>
      );

      return (
        <TruncateTooltip
          atLength={14}
          text={parsedInput}
          fallbackLabel={inputLabel}
          truncatedLabelWrapper={inputLabel}
        />
      );
    },
  };

  const industryColumn: ColumnDef<AllEntitiesResolved> = {
    id: 'industry',
    header: t('watchlistManager:industryColumnLabel'),
    cell: props => {
      const item = props.row.original;

      /* i18next-extract-disable-next-line */
      const industryName = t([
        `widget:assetOverview.companyProfile.sectorNames.${item.gics?.industry_group.code}`,
        'widget:assetOverview.companyProfile.sectorNames.notFound',
      ]);

      return (
        <WithNAFallback
          check={!!item.gics}
          value={
              <TruncateTooltip
                atLength={17}
                text={industryName}
                truncatedLabelWrapper={
                  <span className={styles.text}>{industryName}</span>
                }
              />
          }
        />
      );
    },
  };

  const columns: ColumnDef<AllEntitiesResolved>[] = [
    {
      id: 'rowSelected',
      header: ({ table }: { table: Table<AllEntitiesResolved> }) => (
        <RowSelectionCheckbox
          name="watchlist-select"
          onChange={(_, e) => toggleAllRowsSelection(e.target.checked)}
          checked={table.getIsAllRowsSelected()}
        />
      ),
      cell: () => <span />,
    },
    ...(optionalColumns.inputColumn ? [inputColumn] : []),
    {
      id: 'entity',
      header: t('watchlistManager:tickerColumnLabel'),
      cell: props => (
        <EntityCell entity={props.row.original} handlers={handlers} />
      ),
    },
    {
      id: 'name',
      header: t('watchlistManager:nameColumnLabel'),
      cell: props => {
        const item = props.row.original;

        return (
          <WithNAFallback
            check={!!item.name}
            value={
              <TruncateTooltip
                atLength={17}
                text={item.name!}
                truncatedLabelWrapper={
                  <span className={styles.text}>{item.name}</span>
                }
              />
            }
          />
        );
      },
    },
    ...(optionalColumns.industryColumn ? [industryColumn] : []),
    {
      id: 'type',
      header: t('watchlistManager:typeColumnLabel'),
      cell: props => {
        const item = props.row.original as Entity;

        const { sub_class } = mapAssetClass(item);
        /* i18next-extract-disable-next-line  */
        const type = t(`analyze:assetSubClass.${sub_class}`);
        return (
          <WithNAFallback
            check={(!!item.sub_class || !!item.asset_class) && !!sub_class}
            value={
              <TruncateTooltip
                atLength={17}
                text={type}
                truncatedLabelWrapper={
                  <span className={styles.text}>{type}</span>
                }
              />
            }
          />
        );
      },
    },
  ];

  return columns;
};
